import { VdsBasicInputCustomEvent } from '@volt/volt-basic-components';
import { VdsBasicTextarea, VdsIcon } from '@volt/volt-extended-components-react';
import { useCallback, useMemo } from 'react';
import { FieldValues, UseControllerProps, useController } from 'react-hook-form';
import TogglePassword from '@cas/components/inputs/toggle-password/TogglePassword';
import VdsBasicInputSafe from '@cas/components/inputs/vds-basic-input-safe/VdsBasicInputSafe';
import Field from '../field/Field';

export interface CasFormInputProps<TFormValues extends FieldValues>
  extends UseControllerProps<TFormValues> {
  label?: string;
  id?: string;
  placeholder?: string;
  isReadonly?: boolean;
  element?: 'input' | 'textarea';
  type?: 'number' | 'text' | 'password' | 'email';
  size?: 'medium' | 'large';
  height?: string;
  minHeight?: string;
  maxHeight?: string;
  secondaryActionButton?: string;
  onFocusOut?: () => void;
  onEnterPressed?: () => void;
  autocomplete?: string;
  helperText?: string;
  isRequired?: boolean;
  tooltipLabel?: string;
}

export function CasFormInput<TFormValues extends FieldValues>({
  size = 'medium',
  id = '',
  isRequired = false,
  helperText,
  tooltipLabel,
  ...props
}: CasFormInputProps<TFormValues>) {
  // Don't pass disabled property into useController! See https://github.com/react-hook-form/react-hook-form/issues/12758
  const { disabled, ...sanitisedProps } = props;

  const { field, fieldState, formState } = useController(sanitisedProps);

  const onBlur = useCallback(() => {
    field.onBlur();

    if (props.onFocusOut) {
      props.onFocusOut();
    }
  }, [field, props]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && props.onEnterPressed) {
        event.preventDefault();
        props.onEnterPressed();
      }
    },
    [props],
  );

  const isError = !(props.disabled || field.disabled) && fieldState.error !== undefined;
  const errorMessage = isError ? fieldState.error?.message : undefined;
  const isDisabled = props.disabled || field.disabled || formState.isSubmitting;

  const VdsCommonProps = useMemo(() => {
    return {
      error: isError,
      disabled: isDisabled,
      isReadonly: props.isReadonly,
      value: field.value,
      onBlur,
    };
  }, [field.value, onBlur, isError, isDisabled, props.isReadonly]);

  const VdsTextAreaProps = useMemo(() => {
    return {
      ...VdsCommonProps,
      /**
       * The VDS height prop is not handled well in the base component.
       * Using it leads to a text overflow beyond the height of the textarea unless autogrow is set to false in tandem.
       * This is because VDS does not correctly calculate height to factor in the padding nor use the correct box-sizing attribute.
       * This behaviour has been specified in the VDS docs, so change is unlikely:
       * https://volt.pages.corp.wabtec.com/volt-basic-components/master/storybook/?path=/docs/vds-components-textarea-basic-textarea--docs
       */
      height: props.height,
      // autogrow must be false if a height has been specified, and must be true for minHeight and maxHeight.
      autogrow: typeof props.height !== 'undefined' ? false : true,
      minHeight: props.minHeight,
      maxHeight: props.maxHeight,
      placeholder: props.placeholder,
      onKeyDown: onKeyDown,
      size,
      id: id || undefined,
      onChangeInput: field.onChange,
    };
  }, [
    VdsCommonProps,
    props.height,
    props.minHeight,
    props.maxHeight,
    props.placeholder,
    onKeyDown,
    size,
    id,
    field.onChange,
  ]);

  /**
   * This onChangeNumberInput function should not perform any string or number validation.
   * It's sole purpose is to convert the string event value of a number input into a Number, even if it returns a NaN value.
   */
  const onChangeNumberInput = useCallback(
    (event: VdsBasicInputCustomEvent<string>) => {
      field.onChange(Number(event.detail));
    },
    [field],
  );

  const VdsInputProps = useMemo(() => {
    return {
      ...VdsCommonProps,
      type: props.type ?? 'text',
      name: props.name,
      placeholder: props.placeholder,
      onKeyDown: onKeyDown,
      size,
      id: id || undefined,
      autocomplete: props.autocomplete ?? 'off',
      // Conditionally switch out the default onChange handler with a string to number converter.
      onChangeInput: props.type === 'number' ? onChangeNumberInput : field.onChange,
    };
  }, [
    VdsCommonProps,
    props.type,
    props.name,
    props.placeholder,
    props.autocomplete,
    onKeyDown,
    size,
    id,
    onChangeNumberInput,
    field.onChange,
  ]);

  return (
    <Field
      key={props.name}
      uid={id || props.name}
      label={props.label ?? ''}
      tooltipLabel={tooltipLabel}
      errorMessage={errorMessage}
      helperText={helperText}
      isDisabled={isDisabled}
      isRequired={isRequired}
    >
      {props.element === 'textarea' ? (
        <VdsBasicTextarea {...VdsTextAreaProps} minHeight="100px"></VdsBasicTextarea>
      ) : (
        <>
          {VdsInputProps.type === 'password' && (
            <TogglePassword {...VdsInputProps} type="password" />
          )}
          {VdsInputProps.type !== 'password' && (
            <VdsBasicInputSafe {...VdsInputProps}>
              {props.secondaryActionButton && (
                <VdsIcon slot="secondaryActionIcon" svgIcon={props.secondaryActionButton} />
              )}
            </VdsBasicInputSafe>
          )}
        </>
      )}
    </Field>
  );
}

export default CasFormInput;
